import { get, isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import { NotificationDetail, Task } from '..';
import { circle_checked_icon, nofication } from '../../assets/img/';
import { useComponentVisible } from '../../hooks';
import { meetingApi } from '../../services/tcMeeting';
import { MEETING_DETAIL_STATUS, NOTIFICATION_MEETING_TYPE, NOTIFICATION_PIPELINE } from '../../utils/constants';
import styles from './notification.module.scss';

const Notification = () => {
  const [tabSelected, setTabSelected] = useState('Notification Detail');
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [tasks, setTasks] = useState({ data: [], total: 0, page: 1, limit: 10, loading: false, hasMore: true });
  const history = useHistory();
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
  const { roleActive } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);

  const countNotifications = async () => {
    try {
      const response = await meetingApi.countNotifications(roleActive);
      const rs = get(response, 'data.result.total_unseen') || 0;
      setTotalNotifications(rs);
      const bell = document.getElementById('header-notification');
      if (bell && rs > 0) {
        bell.classList.add('shake');
        setTimeout(() => {
          bell.classList.remove('shake');
        }, 500);
      }
    } catch (error) {
      console.error(error);
      setTotalNotifications(0);
    }
  };

  const getNotifications = async (isAsRead) => {
    try {
      setLoading(true);
      const response = await meetingApi.getNotifications(roleActive, isAsRead);
      const rs = get(response, 'data.result.notifications');
      setNotifications(rs || []);
    } catch (error) {
      setNotifications([]);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTasks = async () => {
    try {
      setTasks((prev) => ({ ...prev, loading: true }));
      const response = await meetingApi.getTasks({ page: tasks.page, limit: tasks.limit, roleId: roleActive.roleId });
      const result = get(response, 'data.result.data');
      if (isEmpty(result)) return setTasks((prev) => ({ ...prev, hasMore: false }));
      if (tasks.page > 1) {
        setTasks((prev) => ({ ...prev, data: [...prev.data, ...result], hasMore: true }));
      } else {
        setTasks((prev) => ({ ...prev, data: !isEmpty(result) ? result : [], hasMore: true }));
      }
    } catch (error) {
      console.error(error);
      setTasks({ data: [], total: 0, page: 1, limit: 10, loading: false, hasMore: true });
    } finally {
      setTasks((prev) => ({ ...prev, loading: false }));
    }
  };

  const viewNotification = async (notificationItem) => {
    try {
      if (!notificationItem || notificationItem.id === undefined) return;
      meetingApi.updateSeenNotification(notificationItem.id);
      countNotifications();
      setIsComponentVisible(false);
      if (
        [NOTIFICATION_MEETING_TYPE.TALENT_FINDER_EXPORT, NOTIFICATION_MEETING_TYPE.POSITION_FINDER_EXPORT].includes(
          notificationItem.notification_type
        )
      ) {
        window.open(notificationItem.description, '_blank');
      } else if (notificationItem.notification_type === 'AI Pipeline Run Failed') {
        history.replace(`/admin/audit-logs?type=${NOTIFICATION_PIPELINE.AI_PIPELINE}&page_no=${notificationItem.page_no}&date=${Date.now()}`);
      } else if (notificationItem.notification_type === 'Data Pipeline Run Failed') {
        history.replace(`/admin/audit-logs?type=${NOTIFICATION_PIPELINE.DATA_PIPELINE}&page_no=${notificationItem.page_no}&date=${Date.now()}`);
      } else if (
        [NOTIFICATION_MEETING_TYPE.SUCCESSOR_ATTRITION, NOTIFICATION_MEETING_TYPE.SUCCESSOR_PROMOTION].includes(notificationItem.notification_type)
      ) {
        const description = JSON.parse(notificationItem?.description);
        history.replace(`/succession-planning-dashboard/${description?.position_code}?successor_type=${notificationItem?.notification_type}`);
      } else {
        if (notificationItem?.type !== 'PDC') {
          const path =
            notificationItem.notification_type === NOTIFICATION_MEETING_TYPE.SUCCESSION_PLANNING_SYNDICATION
              ? `meeting/${notificationItem.meeting_id}/agenda`
              : 'meeting/detail';
          history.push(`/${path}/${notificationItem.ref_id}`);
        }
        if (notificationItem?.type === 'PDC') {
          history.push(`/pdc-meeting/${notificationItem.meeting_id}`);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const viewTask = (task) => {
    try {
      if (!task || task.meeting_id === undefined) return;
      setIsComponentVisible(false);
      let path;
      if (task.on_step === 1) {
        path = task?.type === 'PDC' ? `/pdc-meeting/${task.meeting_pdc_id}` : `/meeting/${task.meeting_id}`;
      }
      if (task.on_step === 2) {
        path = task?.type === 'PDC' ? `/pdc-meeting/${task.meeting_pdc_id}/mobilities` : `/meeting/${task.meeting_id}/agendas`;
      }
      if (task.status === MEETING_DETAIL_STATUS.IN_REVIEW) {
        path = task?.type === 'PDC' ? `/pdc-meeting/${task.meeting_pdc_id}/in-review` : `/meeting/detail/${task.meeting_id}`;
      }
      history.push(path);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    countNotifications();
    getNotifications(false);
    const interval = setInterval(() => {
      countNotifications();
      getNotifications(false);
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isComponentVisible) {
      getNotifications(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComponentVisible]);

  useEffect(() => {
    if (isComponentVisible && tasks.page && tabSelected !== 'Notification Detail') {
      getTasks();
    }
    if (tabSelected === 'Notification Detail') setTasks({ data: [], total: 0, page: 1, limit: 10, loading: false, hasMore: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComponentVisible, tasks.page, tabSelected]);

  useEffect(() => {
    if (!isComponentVisible) {
      setTabSelected('Notification Detail');
      setNotifications([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComponentVisible]);

  return (
    <div className={styles.container} ref={ref}>
      <div
        className={styles.nofication}
        onKeyDown={() => {}}
        onClick={() => setIsComponentVisible(!isComponentVisible)}
        data-testid="icon"
        id="header-notification"
      >
        <img src={nofication} alt="nofication" />
        {totalNotifications !== 0 && <div className={styles.nofication_number}>{totalNotifications}</div>}
      </div>
      {isComponentVisible && (
        <div className={styles.detail}>
          <div className={styles.notification_head}>
            <h3 className={styles.notification_head_title}>Notification</h3>
            <div
              className={styles.notification_as_read}
              onClick={() => {
                getNotifications(true);
                countNotifications();
              }}
            >
              <img src={circle_checked_icon} alt="" />
              <span>Mark all as read</span>
            </div>
          </div>
          <div className="d-flex justify-content-start align-items-center" style={{ gap: '10px', marginBottom: '20px' }}>
            <div
              className={'Notification Detail' === tabSelected ? styles.active : null}
              style={{ padding: '10px 15px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onKeyDown={() => {}}
              onClick={() => setTabSelected('Notification Detail')}
            >
              Notification
              {totalNotifications !== 0 && (
                <div
                  style={{
                    width: '25px',
                    height: '25px',
                    fontSize: '13px',
                    color: '#fff',
                    backgroundColor: '#00847C',
                    borderRadius: '50%',
                    marginLeft: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {totalNotifications}
                </div>
              )}
            </div>
            <div
              className={'Task' === tabSelected ? styles.active : null}
              style={{ padding: '5px 10px', cursor: 'pointer' }}
              onKeyDown={() => {}}
              onClick={() => setTabSelected('Task')}
            >
              Task
            </div>
          </div>
          {tabSelected === 'Notification Detail' ? (
            <NotificationDetail notifications={notifications} viewNotification={viewNotification} loading={loading} />
          ) : (
            <Task tasks={tasks} viewTask={viewTask} setTasks={setTasks} />
          )}
        </div>
      )}
    </div>
  );
};

export default withRouter(Notification);
